import React, { useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaLinkedin } from "react-icons/fa";

const Contact = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const revibeAddress =
    "2206, 22nd Floor, Solus, Hiranandani Estate, Patlipada, Near Hiranandani School, Thane (West), Maharashtra – 400607.";
  const mapsLink =
    "https://www.google.com/maps/place/Revibe+Health+And+Happiness/@19.2616341,72.9823076,807m/data=!3m2!1e3!4b1!4m6!3m5!1s0x3be7bbd5d9be9415:0xb243579f040455b0!8m2!3d19.2616291!4d72.9848825!16s%2Fg%2F11xvg08kyl?hl=en&entry=ttu&g_ep=EgoyMDI1MDkxMC4wIKXMDSoASAFQAw%3D%3D";
  const mapUrl =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.112910379734!2d72.9823076!3d19.2616341!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7bbd5d9be9415%3A0xb243579f040455b0!2sRevibe%20Health%20And%20Happiness!5e0!3m2!1sen!2sin!4v1695468900000!5m2!1sen!2sin";

  return (
    <section className="bg-[#f8f5f3]">
      {/* Page Title */}
      <div className="text-center py-10">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
          Contact Us
        </h1>
      </div>

      {/* Google Map with Location Button & Modal */}
      <div className="w-full h-[300px] md:h-[400px] relative">
        {/* Map iframe */}
        <iframe
          title="Revibe Mumbai Office Location"
          src={mapUrl}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>

        {/* Modal popup */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm transform transition-all sm:my-8 sm:align-middle sm:max-w-md">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-semibold text-gray-900">
                  Revibe Head Office
                </h3>
                <button
                  onClick={toggleModal}
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </button>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600">{revibeAddress}</p>
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact Info + Form */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 py-12 px-6 sm:px-10 lg:px-16">
        {/* Left Section - Contact Info */}
        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-md">
          <h2 className="text-2xl font-semibold mb-2">📞 Customer Support</h2>
          <div className="space-y-4 text-gray-700 mb-6">
            <p className="flex items-center gap-3">
              <Phone className="text-lime-600" /> Phone (India HQ):
              +91 99677 22756
            </p>
            <p className="flex items-center gap-3">
              <Mail className="text-lime-600" /> Email (Support):
              hello@getrevibe.app
            </p>
            <p className="text-gray-600">
              ⏰ Working Hours: Mon – Sat, 9:30 AM – 6:00 PM IST <br />
              (Closed on Sundays & Public Holidays)
            </p>
          </div>
          <h2 className="text-2xl font-semibold mb-2">🏢 Company Details</h2>
          <p className="flex items-start gap-3 leading-relaxed text-gray-700">
            <MapPin className="text-lime-600 shrink-0" />
            Revibe Health and Happiness <br />
            2206, 22nd Floor, Solus, Hiranandani Estate, Patlipada, <br />
            Near Hiranandani School, Thane (West), Maharashtra – 400607.
          </p>
          {/* Social Links */}
          <div className="mt-6">
            <div className="flex space-x-4">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/revibeapp_"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
              >
                <FaInstagram className="text-pink-500 text-xl" />
              </a>

              {/* Facebook */}
              <a
                href="https://www.facebook.com/people/Revibe-Health-And-Happiness"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
              >
                <FaFacebook className="text-blue-600 text-xl" />
              </a>

              {/* Twitter */}
              <a
                href="https://x.com/revibe2025"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
              >
                <FaTwitter className="text-sky-500 text-xl" />
              </a>

              {/* YouTube */}
              <a
                href="https://www.youtube.com/@Revibe2025"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
              >
                <FaYoutube className="text-red-600 text-xl" />
              </a>
              <a
                href="https://www.linkedin.com/company/revibe-health-and-happiness/"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
              >
                <FaLinkedin className="text-blue-600 text-xl" />
              </a>
            </div>
          </div>
        </div>

        {/* Right Section - Contact Form */}
        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-md">
          <h2 className="text-2xl font-semibold mb-2">📬 Quick Contact Form</h2>
          <p className="text-gray-600 mb-6">
            You can also reach us by filling out the contact form below. Our
            team will respond within 24–48 business hours.
          </p>
          <form className="space-y-4">
            <input
              type="text"
              placeholder="Name"
              className="border border-gray-300 rounded-full px-4 py-3 text-sm outline-none focus:border-lime-500 w-full"
              required
            />
            <input
              type="email"
              placeholder="Email Address"
              className="border border-gray-300 rounded-full px-4 py-3 text-sm outline-none focus:border-lime-500 w-full"
              required
            />
            <input
              type="tel"
              placeholder="Phone Number"
              className="border border-gray-300 rounded-full px-4 py-3 text-sm outline-none focus:border-lime-500 w-full"
            />
            <input
              type="text"
              placeholder="Subject"
              className="border border-gray-300 rounded-full px-4 py-3 text-sm outline-none focus:border-lime-500 w-full"
            />
            <textarea
              placeholder="Message"
              className="border border-gray-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-500 w-full min-h-[120px]"
              required
            ></textarea>
            <button
              type="submit"
              className="bg-black text-white w-full sm:w-auto px-6 py-3 rounded-full hover:bg-gray-800 transition"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
